import React, { FC, useContext, useState } from 'react';

import moment from 'moment/moment';
import * as constants from 'utils/constants';

import { Identification, UserGender } from '../../../../../api-client';
import { useEffectOnMount, useEffectOnUpdate, useProfileData } from '../../../../../hooks';
import { ProfileState } from '../../../../../store/profile';
import {
  Errors as ValidationErrors,
  inputFilters,
  isEmail,
  numbersOnly,
} from '../../../../../utils';
import { ymAsync } from '../../../../../utils/functions/ymWrapper';
import {
  CommonInput,
  Heading,
  InputErrors,
  RadioButton,
  SecondaryHeading,
} from '../../../../common';
import RadioButtonGroup, {
  Direction,
} from '../../../../common/form/RadioButtonGroup/RadioButtonGroup';
import { HeadingType } from '../../../../common/typography/headings/Heading/Heading';
import IdentificationContext from '../../../../context/IdentificationContext';
import { IdentificationDateSelect } from '../../../../pages/identification';
import EntrepreneurScanUpload from '../../../../pages/identification/EntrepreneurScanUpload';
import { IdentificationBlock } from '../index';

export type IdentificationPersonalErrors = {
  firstName?: ValidationErrors;
  middleName?: ValidationErrors;
  lastName?: ValidationErrors;
  inn?: ValidationErrors;
  ogrnip?: ValidationErrors;
  birthday?: ValidationErrors;
  email?: ValidationErrors;
  birthplace?: ValidationErrors;
  registerAddress?: ValidationErrors;
  entrepreneurDocument?: ValidationErrors;
};

interface Props {
  isEntrepreneur?: boolean;
  isLegalEntity?: boolean;
  onValidated: (value: boolean) => void;
  identification: Identification | undefined;
}

const IdentificationPersonal: FC<Props> = ({
  isEntrepreneur,
  isLegalEntity,
  onValidated,
  identification,
}) => {
  const [errors, setErrors] = useState<IdentificationPersonalErrors>({});
  const context: any = useContext(IdentificationContext);
  const profile: ProfileState = useProfileData();
  const isVerified = () => {
    return profile.lenderVerified;
  };
  useEffectOnMount(() => {
    const person = identification?.person;
    if (identification && person) {
      context.setFirstName(person.firstName);
      context.setMiddleName(person.middleName);
      context.setLastName(person.lastName);
      context.setInn(identification.inn.replaceAll(' ', ''));

      if (profile.email) {
        context.setEmail(profile.email.email);
      }
      context.setBirthplace(person.birthplace);
      context.setBirthday(moment(person.birthday).toDate());
      context.setRegisterAddress(identification.registerAddress);
      if (identification.ogrnip) {
        context.setOgrnip(identification.ogrnip);
      }
      if (identification.entrepreneurDocument) {
        context.setEntrepreneurDocument(identification.entrepreneurDocument);
      }
    }
  });

  const validateBlock = (): boolean => {
    const errors: IdentificationPersonalErrors = {};
    if (context.firstName === '') {
      errors.firstName = ['Имя обязательно для заполнения'];
    }
    if (context.lastName === '') {
      errors.lastName = ['Фамилия обязательна для заполнения'];
    }
    if (context.inn === '') {
      errors.inn = ['ИНН обязателен для заполнения'];
    } else if (!numbersOnly(context.inn)) {
      errors.inn = ['ИНН должен состоять из цифр'];
    } else if (context.inn.length !== constants.INN_INDIVIDUAL_LENGTH) {
      errors.inn = [`Длина ИНН - ${constants.INN_INDIVIDUAL_LENGTH} символов`];
    }
    if (isEntrepreneur) {
      if (context.ogrnip === '') {
        errors.ogrnip = ['ОГРНИП обязателен для заполнения'];
      } else if (!numbersOnly(context.ogrnip)) {
        errors.ogrnip = ['ОГРНИП должен состоять из цифр'];
      } else if (context.ogrnip.length !== constants.MAX_OGRNIP_LENGTH) {
        errors.ogrnip = [`Длина ОГРНИП - ${constants.MAX_OGRNIP_LENGTH} символов`];
      }
      if (context.entrepreneurDocument.length === 0) {
        errors.entrepreneurDocument = ['Приложите файл со сканом документа'];
      }
    }
    //TODO: ADD BIRTHDAY ERRORS
    if (context.email === '') {
      errors.email = ['Email обязателен для заполнения'];
    } else if (!isEmail(context.email)) {
      errors.email = ['Неверный формат email'];
    }
    if (context.birthplace === '') {
      errors.birthplace = ['Место рождения обязательно для заполнения'];
    }
    if (context.registerAddress === '') {
      errors.registerAddress = ['Место регистрации обязательно для заполнения'];
    } else if (context.registerAddress.length < constants.MIN_REGISTRATION_LENGTH) {
      errors.registerAddress = ['Низкий уровень детализации адреса'];
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const personalHeading = () => {
    if (isLegalEntity) {
      return (
        <SecondaryHeading>
          <Heading headingType={HeadingType.H2}>
            Персональные данные руководителя юридического лица
          </Heading>
        </SecondaryHeading>
      );
    } else {
      return (
        <SecondaryHeading>
          <Heading headingType={HeadingType.H2}>Персональные данные</Heading>
        </SecondaryHeading>
      );
    }
  };
  const isPersonalDataChanged =
    context.middleName !== identification?.person?.middleName ||
    context.firstName !== identification?.person?.firstName ||
    context.lastName !== identification?.person?.lastName ||
    context.inn !== identification?.inn ||
    context.registerAddress !== identification?.registerAddress;

  useEffectOnUpdate(() => {
    onValidated(validateBlock());
    if (isVerified() && isPersonalDataChanged) {
      context.setFirstPassportScans([]);
      context.setSecondPassportScans([]);
      context.setThirdPassportScans([]);
    }
  }, [
    context.middleName,
    context.firstName,
    context.lastName,
    context.inn,
    context.ogrnip,
    context.email,
    context.birthplace,
    context.registerAddress,
    context.birthday,
    context.entrepreneurDocument,
  ]);
  return (
    <IdentificationBlock>
      {personalHeading()}
      <div className="form__group">
        <CommonInput
          value={context.lastName}
          onChange={(value) => context.setLastName(value)}
          onBlur={() => ymAsync('verification-surname-input')}
          title="Фамилия"
          id="lastName"
          inputFilter={inputFilters.russianName}
          required
        />
        {errors.lastName && <InputErrors errors={errors.lastName} />}
      </div>
      <div className="form__group">
        <CommonInput
          value={context.firstName}
          onChange={(value) => context.setFirstName(value)}
          onBlur={() => ymAsync('verification-name-input')}
          title="Имя"
          id="firstName"
          inputFilter={inputFilters.russianName}
          required
        />
        {errors.firstName && <InputErrors errors={errors.firstName} />}
      </div>
      <div className="form__group">
        <CommonInput
          value={context.middleName}
          onChange={(value) => {
            context.setMiddleName(value);
          }}
          onBlur={() => ymAsync('verification-patronymic-input')}
          title="Отчество"
          id="middleName"
          inputFilter={inputFilters.russianName}
          required={false}
        />
        {errors.middleName && <InputErrors errors={errors.middleName} />}
      </div>
      <div className="form__group">
        <CommonInput
          value={context.inn}
          onChange={(value) => context.setInn(value)}
          onBlur={() => ymAsync('verification-inn-input')}
          title="ИНН"
          id="inn"
          inputFilter={inputFilters.digits}
          required
        />
        {errors.inn && <InputErrors errors={errors.inn} />}
      </div>
      <div className="form__group">
        <IdentificationDateSelect
          header="Дата рождения"
          value={context.birthday}
          onSelected={context.setBirthday}
        />
        {errors.birthday && <InputErrors errors={errors.birthday} />}
      </div>
      {!isVerified() && (
        <div className="form__group">
          <CommonInput
            value={context.email}
            onChange={(value) => context.setEmail(value)}
            onBlur={() => ymAsync('verification-email-input')}
            title="Email"
            id="email"
            required
            disabled={profile.lenderVerified}
          />
          {errors.email && <InputErrors errors={errors.email} />}
        </div>
      )}
      <div className="form__group">
        <label className="form__label" htmlFor="gender">
          Пол
        </label>
        <RadioButtonGroup direction={Direction.HORIZONTAL}>
          <RadioButton
            id="gender_male"
            onChange={() => context.setGender(UserGender.MALE)}
            checked={context.gender === UserGender.MALE}
          >
            Мужской
          </RadioButton>
          <RadioButton
            id="gender_female"
            onChange={() => context.setGender(UserGender.FEMALE)}
            checked={context.gender === UserGender.FEMALE}
          >
            Женский
          </RadioButton>
        </RadioButtonGroup>
      </div>
      <div className="form__group">
        <CommonInput
          value={context.birthplace}
          onChange={(value) => context.setBirthplace(value)}
          title="Место рождения"
          id="birthplace"
          inputFilter={inputFilters.russianText}
          required
        />
        {errors.birthplace && <InputErrors errors={errors.birthplace} />}
      </div>
      <div className="form__group">
        <CommonInput
          value={context.registerAddress}
          onChange={(value) => context.setRegisterAddress(value)}
          title="Адрес регистрации"
          id="registerAddress"
          inputFilter={inputFilters.russianText}
          required
        />
        {errors.registerAddress && <InputErrors errors={errors.registerAddress} />}
      </div>
      {isEntrepreneur && (
        <div>
          <div className="form__group">
            <CommonInput
              value={context.ogrnip}
              onChange={(value) => context.setOgrnip(value)}
              onBlur={() => ymAsync('verification-ogrnip-input')}
              title="ОГРНИП"
              id="ogrnip"
              inputFilter={inputFilters.digits}
              required
            />
            {errors.ogrnip && <InputErrors errors={errors.ogrnip} />}
          </div>
          <div className="identification__passport-files">
            <div className="identification__file-name">
              Загрузите документ, подтверждающий статус ИП
            </div>
          </div>
          <div className="identification__passport-files-grid">
            <div>
              <EntrepreneurScanUpload />
              {errors.entrepreneurDocument && <InputErrors errors={errors.entrepreneurDocument} />}
            </div>
            <div className="identification-files__warning">
              Загружайте изображение в формате jpg, png или pdf. Файл не должен превышать{' '}
              <span className="identification-files__warning-size"> 4 Мб.</span>
            </div>
          </div>
        </div>
      )}
    </IdentificationBlock>
  );
};

export default IdentificationPersonal;
